import React, { useState, useEffect, useRef } from 'react';

const Subtitles = ({ 
  detectedSpeech, 
  currentSpeechConfidence, 
  isRecording, 
  speechRecognitionSupported, 
  questionState, 
  subtitleText,
  subtitleTimeouts // Array of timeout values in milliseconds for each line
}) => {
  // Show subtitles if we have subtitle text OR if we're recording with speech recognition
  const shouldShow = subtitleText || (speechRecognitionSupported && isRecording);
  
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const timeoutRefs = useRef([]);
  
  // Reset and start displaying lines when subtitleText or timeouts change
  useEffect(() => {
    // Split subtitle text into lines and filter out blank lines
    const lines = subtitleText 
      ? subtitleText.replace(/\\n/g, '\n').split('\n').filter(line => line.trim().length > 0)
      : [];
    
    if (!subtitleText || lines.length === 0) {
      setCurrentLineIndex(0);
      // Clear all timeouts
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
      return;
    }
    
    // Reset state when subtitle text changes - show first line immediately
    setCurrentLineIndex(0);
    
    // Clear any existing timeouts
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = [];
    
    // If timeouts are provided, display lines sequentially (one at a time)
    // Only schedule timeouts for transitions between lines (not for the final line)
    // The final line will remain visible until subtitleText changes (when audio ends)
    if (subtitleTimeouts && subtitleTimeouts.length > 0 && lines.length > 1) {
      // Schedule transitions between lines (we need lines.length - 1 timeouts)
      let cumulativeDelay = 0;
      const numTransitions = lines.length - 1;
      
      for (let i = 0; i < numTransitions; i++) {
        // Use the timeout at index i, or fall back to the last timeout if not enough provided
        const timeout = subtitleTimeouts[i] ?? subtitleTimeouts[subtitleTimeouts.length - 1] ?? 2000;
        cumulativeDelay += timeout;
        
        const timeoutId = setTimeout(() => {
          setCurrentLineIndex(i + 1); // Move to next line (replacing the previous)
        }, cumulativeDelay);
        
        timeoutRefs.current.push(timeoutId);
      }
      // Note: The last line (lines[lines.length - 1]) will remain visible
      // until subtitleText changes, which happens when audio ends
    } else {
      // No timeouts provided or single line - display all lines at once (backward compatibility)
      // This maintains the old behavior when timeouts aren't specified
      setCurrentLineIndex(0);
    }
    
    // Cleanup function
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
    };
  }, [subtitleText, subtitleTimeouts]);
  
  if (!shouldShow) {
    return null;
  }

  // SBS style subtitle text styling - yellow with black border
  const subtitleTextStyle = {
    color: '#FFFF00',
    textShadow: `
      -1px -1px 0 #000,
      1px -1px 0 #000,
      -1px 1px 0 #000,
      1px 1px 0 #000,
      -2px -2px 0 #000,
      2px -2px 0 #000,
      -2px 2px 0 #000,
      2px 2px 0 #000
    `,
    fontWeight: 'bold'
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '150px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'transparent',
      padding: '15px 25px',
      textAlign: 'center',
      zIndex: 1001,
      maxWidth: '80%',
      fontSize: '18px'
    }}>
      {subtitleText ? (
        <div style={subtitleTextStyle}>
          {(() => {
            // Split subtitle text into lines and filter out blank lines
            const lines = subtitleText.replace(/\\n/g, '\n').split('\n').filter(line => line.trim().length > 0);
            // Display only the current line
            const currentLine = lines[currentLineIndex] || '';
            
            // If timeouts are provided, show only current line
            if (subtitleTimeouts && subtitleTimeouts.length > 0) {
              return (
                <div>
                  {currentLine}
                </div>
              );
            } else {
              // No timeouts - show all lines (backward compatibility)
              return lines.map((line, index, array) => (
                <div key={index} style={{ marginBottom: index < array.length - 1 ? '0.5em' : '0' }}>
                  {line}
                </div>
              ));
            }
          })()}
        </div>
      ) : detectedSpeech ? (
        <div>
          <div style={{ ...subtitleTextStyle, marginBottom: '10px' }}>
            "{detectedSpeech}"
          </div>
          <div style={{ 
            width: '100%',
            marginTop: '8px'
          }}>
            {/* Progress bar container */}
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Progress bar fill */}
              <div style={{
                width: `${(currentSpeechConfidence * 100)}%`,
                height: '100%',
                backgroundColor: currentSpeechConfidence >= 0.8 ? '#28a745' : currentSpeechConfidence >= 0.5 ? '#ffc107' : '#dc3545',
                transition: 'width 0.2s ease, background-color 0.2s ease',
                borderRadius: '3px'
              }} />
            </div>
          </div>
        </div>
      ) : (
        <div style={{ ...subtitleTextStyle, opacity: 0.7 }}>
          {questionState === 'playing' ? 'Listening to question...' : 'Speak now...'}
        </div>
      )}
    </div>
  );
};

export default Subtitles;
